import * as THREE from 'three/webgpu';
import { isFeatureFace, isFeatureLine } from '../../mesh/feature';
import { resolveBRepGeometryDescriptor, resolveTopologySelectionPick } from '../../utils/feature';
import { resolveOwningFeatureResultMesh } from '../../utils/feature/resolveFeatureSceneMesh';
import type { MeasureMetricQueryContext } from '../types/measure-property-types';

/**
 * 构建度量查询上下文。
 *
 * 设计说明：供测量属性解析与求距几何适配共用，避免重复解析拓扑快照。
 *
 * @param sceneObject 场景对象。
 * @param topologyPick 拓扑命中快照。
 * @param brepDescriptor BRep 几何描述符。
 * @returns 度量上下文。
 */
export function buildMeasureMetricQueryContext(
    sceneObject: THREE.Object3D,
    topologyPick: ResolvedTopologyPick | null,
    brepDescriptor: BRepGeometryDescriptor | null,
): MeasureMetricQueryContext {
    const resultMesh = resolveOwningFeatureResultMesh(sceneObject);
    let edgeSnapshot: TopologySnapshotEdge | null = null;
    let faceSnapshot: TopologySnapshotFace | null = null;

    if (resultMesh && isFeatureLine(sceneObject) && typeof sceneObject.edgeIndex === 'number') {
        edgeSnapshot = resultMesh.getTopologyEdgeSnapshotByIndex(sceneObject.edgeIndex);
    } else if (resultMesh && topologyPick?.kind === 'edge') {
        edgeSnapshot = resultMesh.getTopologyEdgeSnapshotByIndex(topologyPick.topologyIndex);
    }

    if (resultMesh && isFeatureFace(sceneObject)) {
        faceSnapshot = resultMesh.getTopologyFaceSnapshotByIndex(sceneObject.faceIndex);
    } else if (resultMesh && topologyPick?.kind === 'face') {
        faceSnapshot = resultMesh.getTopologyFaceSnapshotByIndex(topologyPick.topologyIndex);
    }

    return {
        sceneObject,
        topologyPick,
        brepDescriptor,
        edgeSnapshot,
        faceSnapshot,
        topologySnapshot: resultMesh?.getTopologySnapshot() ?? null,
    };
}
